import { DatePicker } from 'antd';
import { Card, TimeRangePickerProps } from 'antd/lib';
import dayjs, { Dayjs } from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import trapMetricApi from '../../apis/TrapMetricApi';
import CustomContext from '../../contexts/CustomContext';
import { CustomAuth, Plot, TrapMetricStats, TrapPlot } from '../../models/Entities';
import alertService from '../../services/AlertService';
import stringService from '../../services/StringService';
import styles from './ChartMetricHumidityComponent.module.css';

/**
 * Returns the chart metric humidity component.
 * @returns the chart metric humidity component
 */
const ChartMetricHumidityComponent: React.FC<Props> = (props) => {
    const { plot, trapPlot } = props;
    const { RangePicker } = DatePicker;

    /*** HOOKS ***/

    const intl = useIntl();
    const [dataset, setDataset] = useState<(string | number | undefined)[][]>([]);
    const [series, setSeries] = useState<string[]>([]);
    const [dimensions, setDimensions] = useState<string[]>([]);
    const [, setLoading] = useState<'loading'>();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;
    const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(14, 'days'));
    const [endDate, setEndDate] = useState<Dayjs>(dayjs());

    /*** EFFECTS ***/

    useEffect(() => {
        const buildChartData = (trapMetricStats: TrapMetricStats[]): any => {
            const trapPlots: TrapPlot[] = trapMetricStats
                .map((tis) => tis.trapPlot)
                .filter((value, index, self) => index === self.findIndex((t) => t.id === value.id))
                .sort((a, b) => stringService.sort(a.trap?.uuid, b.trap?.uuid));
            const series: string[] = trapPlots.map((te) => `${te.trap?.shortUuid}`);
            const dimensions: string[] = ['timestamp', ...series];

            const sortedStats = [...trapMetricStats].sort((a, b) => (a.date.isAfter(b.date) ? 1 : -1));
            const dataset: (string | number | undefined)[][] = sortedStats.map((tis) => {
                const values = trapPlots.map((trapPlot) => (trapPlot.id === tis.trapPlot.id ? tis.avgHumidity : undefined));
                return [tis.date.toISOString(), ...values];
            });

            return { series, dimensions, dataset };
        };
        const init = async () => {
            try {
                setLoading('loading');

                let trapMetricStats: TrapMetricStats[] = [];
                if (auth.companyId && trapPlot && trapPlot.id) {
                    trapMetricStats = await trapMetricApi.listStats(startDate, endDate, 'RAW', auth.companyId, undefined, trapPlot.id);
                } else if (auth.companyId && plot && plot.id) {
                    trapMetricStats = await trapMetricApi.listStats(startDate, endDate, 'RAW', auth.companyId, plot.id);
                } else if (auth.companyId && !plot) {
                    trapMetricStats = await trapMetricApi.listStats(startDate, endDate, 'RAW', auth.companyId);
                }
                const { dimensions, series, dataset } = buildChartData(trapMetricStats);

                setSeries(series);
                setDimensions(dimensions);
                setDataset(dataset);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [intl, auth.companyId, plot, trapPlot, startDate, endDate]);

    /*** METHODS ***/

    const onRangeChange = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
        if (dates) {
            const startDate = dateStrings[0];
            const endDate = dateStrings[1];

            setStartDate(dayjs(startDate));
            setEndDate(dayjs(endDate));
        }
    };

    /*** VISUAL ***/

    const rangePresets: TimeRangePickerProps['presets'] = [
        { label: <FormattedMessage id={`dashboard.range.7.days`} />, value: [dayjs().add(-7, 'd'), dayjs()] },
        { label: <FormattedMessage id={`dashboard.range.14.days`} />, value: [dayjs().add(-14, 'd'), dayjs()] },
        { label: <FormattedMessage id={`dashboard.range.30.days`} />, value: [dayjs().add(-30, 'd'), dayjs()] },
        { label: <FormattedMessage id={`dashboard.range.90.days`} />, value: [dayjs().add(-90, 'd'), dayjs()] }
    ];

    return (
        <Card
            bordered={false}
            className={styles.card}
            title={<FormattedMessage id="dashboard.chart.metric.humidity.title" />}
            extra={
                <RangePicker
                    presets={rangePresets}
                    size="small"
                    onChange={onRangeChange}
                    style={{ maxWidth: 220 }}
                    defaultValue={[startDate, endDate]}
                    allowClear={false}
                />
            }
        >
            <ReactECharts
                option={{
                    legend: {
                        bottom: 0
                    },
                    grid: {
                        top: 10
                    },
                    dataset: {
                        source: dataset,
                        dimensions
                    },
                    xAxis: { type: 'time' },
                    yAxis: {},
                    series: series.map((s) => ({
                        name: s,
                        type: 'line',
                        connectNulls: true,
                        smooth: true,
                        encode: {
                            x: 'timestamp',
                            y: s
                        }
                    })),
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: (value: number | undefined) =>
                            value === undefined
                                ? ''
                                : intl.formatNumber(value, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      unit: 'percent',
                                      unitDisplay: 'short',
                                      style: 'unit'
                                  }),
                        axisPointer: {
                            label: {
                                formatter: function (params: any) {
                                    return dayjs(params.value).format('DD/MM/YYYY HH:mm');
                                }
                            }
                        }
                    }
                }}
            />
        </Card>
    );
};
export default ChartMetricHumidityComponent;

interface Props {
    plot?: Plot;
    trapPlot?: TrapPlot;
}
